import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import '../styles/Admin.css'; // Re-use Admin styles

const UserManagementPage = () => {
    const token = localStorage.getItem('token');
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchUsers = async () => {
        try {
            const res = await axios.get('/api/users', {
                headers: { 'x-auth-token': token }
            });
            setUsers(res.data);
            setLoading(false);
        } catch (err) {
            console.error(err);
            setError('Failed to fetch users. Ensure you have super-admin privileges.');
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
        // eslint-disable-next-line
    }, []);

    const handleRoleChange = async (userId, newRole) => {
        if (!window.confirm(`Change user role to ${newRole}?`)) return;
        try {
            await axios.put(`/api/users/${userId}/role`, { role: newRole }, {
                headers: { 'x-auth-token': token }
            });
            // Optimistic update
            setUsers(users.map(u => u._id === userId ? { ...u, role: newRole } : u));
        } catch (err) {
            alert(err.response?.data?.msg || 'Failed to update role');
        }
    };

    const handleToggleStatus = async (userId, currentStatus) => {
        const action = currentStatus ? 'Deactivate' : 'Activate';
        if (!window.confirm(`${action} this user?`)) return;

        try {
            const res = await axios.put(`/api/users/${userId}/status`, {}, {
                headers: { 'x-auth-token': token }
            });
            // Optimistic update
            setUsers(users.map(u => u._id === userId ? { ...u, isActive: res.data.isActive } : u));
        } catch (err) {
            alert('Failed to update status');
        }
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm('Are you sure you want to PERMANENTLY delete this user? This action cannot be undone.')) return;

        try {
            await axios.delete(`/api/users/${userId}`, {
                headers: { 'x-auth-token': token }
            });
            setUsers(users.filter(u => u._id !== userId));
        } catch (err) {
            alert(err.response?.data?.msg || 'Failed to delete user');
        }
    };

    if (loading) return <div className="loading-spinner"></div>;
    if (error) return <div className="error-message">{error}</div>;

    return (
        <div className="admin-container">
            <div className="admin-header" style={{ display: 'block' }}>
                <h1>User Management</h1>

                <p style={{ maxWidth: '800px', color: 'var(--text-secondary)' }}>
                    Control access levels and manage all registered accounts.
                    The circle next to the name represents the <strong>User Avatar</strong> (generated from their first initial).
                </p>

                <div className="role-legend" style={{ marginTop: '15px', display: 'flex', gap: '20px', flexWrap: 'wrap', fontSize: '0.85rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span className="role-dot super-admin"></span> <strong>Super Admin:</strong> Full Control + User Management
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span className="role-dot admin"></span> <strong>Admin:</strong> Operations (Billing, Jewellery, Customers)
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span className="role-dot co-admin"></span> <strong>Co-Admin:</strong> Assisting Role
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span className="role-dot user"></span> <strong>User:</strong> Standard Customer
                    </div>
                </div>
            </div>

            <style>{`
                .role-dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }
                .role-dot.super-admin { background: linear-gradient(135deg, #1a1a1a, #ffd700); }
                .role-dot.admin { background: linear-gradient(135deg, #b8860b, #daa520); }
                .role-dot.co-admin { background: #64748b; }
                .role-dot.user { background: #e2e8f0; border: 1px solid #ccc; }
            `}</style>

            <div className="admin-content">
                <div className="table-responsive">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email / Mobile</th>
                                <th>Role</th>
                                <th>Status</th>
                                <th>Joined</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(user => (
                                <tr key={user._id} className={!user.isActive ? 'user-deactivated' : ''}>
                                    <td>
                                        <div className="user-name-cell">
                                            <div className="avatar-circle">{user.firstName[0]}</div>
                                            <span>{user.firstName} {user.lastName}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="user-contact">
                                            <div>{user.email || '-'}</div>
                                            <small>{user.mobileNumber}</small>
                                        </div>
                                    </td>
                                    <td>
                                        <select
                                            value={user.role}
                                            onChange={(e) => handleRoleChange(user._id, e.target.value)}
                                            className={`role-select role-${user.role}`}
                                        >
                                            <option value="user">User</option>
                                            <option value="co-admin">Co-Admin</option>
                                            <option value="admin">Admin</option>
                                            <option value="super-admin">Super Admin</option>
                                        </select>
                                    </td>
                                    <td>
                                        <span className={`status-pill ${user.isActive ? 'active' : 'inactive'}`}>
                                            {user.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}</td>
                                    <td>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button
                                                className={`btn-action ${user.isActive ? 'btn-deactivate' : 'btn-activate'}`}
                                                onClick={() => handleToggleStatus(user._id, user.isActive)}
                                            >
                                                {user.isActive ? 'Deactivate' : 'Activate'}
                                            </button>
                                            <button
                                                className="btn-action btn-delete"
                                                onClick={() => handleDeleteUser(user._id)}
                                                style={{ backgroundColor: '#fee2e2', color: '#dc2626' }}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default UserManagementPage;
